import store from 'state/store'

export const inboundTicketToolDefinition = {
  name: 'get_inbound_tickets',
  description:
    'Fetch inbound tickets filtered by status, date range, and sorting. Use this tool when you need to retrieve a list of inbound tickets given certain criteria. The tool returns a JSON array of inbound ticket objects.',
  parameters: {
    type: 'object',
    properties: {
      filter: {
        type: 'object',
        properties: {
          statuses: {
            type: 'array',
            items: { type: 'string' },
            description:
              'List of ticket status IDs to filter by. Options are INCOMPLETE, PRICE_COMPLETE, APPROVED, SCALE_COMPLETE, PAID, VOID, TRUCK_IN_YARD',
          },
          sort: {
            type: 'object',
            properties: {
              field: {
                type: 'string',
                description:
                  "Field name to sort by (e.g. 'ticketId'). Options are ticketId, effectiveDate, customerDisplayName, netWeight, netCost",
              },
              ascending: {
                type: 'boolean',
                description:
                  'Should default to false, but if the user wants to sort ascending, pass true. If they user wants to know the last tickets, pass false.',
              },
            },
          },
          date: {
            type: 'string',
            description: `Start of the date range filter in UTC (ISO-8601) For example: 2024-12-15T00:00:00Z. Today's date is ${new Date().toISOString().split('T')[0]}`,
          },
          endDate: {
            type: 'string',
            description: `End of the date range filter in UTC (ISO-8601) For example: 2024-12-19T00:00:00Z. Today's date is ${new Date().toISOString().split('T')[0]}`,
          },
          search: {
            type: 'string',
            description: 'Search for a specific ticket by ticketId or customerDisplayName or a specific material.',
          },
        },
        required: ['statuses'],
      },
      page: {
        type: 'integer',
        description: 'Page number for pagination.',
      },
      pageSize: {
        type: 'integer',
        description:
          'Number of tickets to return per page. If a user asks for 10 tickets, you should pass 10 here. IF a user asks for the last ticket, pass 1.',
      },
    },
    required: ['filter', 'page', 'pageSize'],
  },
}

const customerToolDefinition = {
  name: 'get_customers',
  description: 'Fetch customers by name or ID. Use this tool when you need information about specific customers.',
  parameters: {
    type: 'object',
    properties: {
      filter: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Search for customers by name or ID',
          },
          sort: {
            type: 'object',
            properties: {
              field: {
                type: 'string',
                description: 'Field to sort by (e.g., "displayName")',
              },
              ascending: {
                type: 'boolean',
                description: 'Sort in ascending order if true',
              },
            },
          },
        },
      },
      page: {
        type: 'integer',
        description: 'Page number for pagination',
      },
      pageSize: {
        type: 'integer',
        description: 'Number of customers per page',
      },
    },
    required: ['filter'],
  },
}

export const materialToolDefinition = {
  name: 'get_materials',
  description: 'Fetch materials by name or ID. Use this tool when you need information about specific materials.',
  parameters: {
    type: 'object',
    properties: {
      filter: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description:
              'Search for materials by name or ID. Try to search less specific if you can in case the user has a typo.',
          },
        },
      },
      page: {
        type: 'integer',
        description: 'Page number for pagination',
      },
      pageSize: {
        type: 'integer',
        description: 'Number of materials per page',
      },
    },
    required: ['filter'],
  },
}

export const purchaseOrderSearchToolDefinition = {
  name: 'get_purchase_orders',
  description:
    'Fetch purchase orders by search term, status, or supplier. Use this tool when you need to find purchase orders. You can use the ID and purchaseorderticketid to link to an ibt.',
  parameters: {
    type: 'object',
    properties: {
      filter: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Search for purchase orders by PO number, customer name, or material name',
          },
          statuses: {
            type: 'array',
            items: { type: 'string' },
            description: 'List of statuses to filter by. Options are Open, Closed, Archived',
          },
          supplierId: {
            type: 'string',
            description: 'ID of the supplier to filter by',
          },
        },
      },
      page: {
        type: 'integer',
        description: 'Page number for pagination',
      },
      pageSize: {
        type: 'integer',
        description: 'Number of purchase orders per page',
      },
    },
    required: ['filter'],
  },
}

export const priceSheetToolDefinition = {
  name: 'create_price_sheet',
  description:
    'Creates a price sheet for a purchase order. Must be done before create_purchase_order. Remember to use the create_purchase_order tool after this.',
  parameters: {
    type: 'object',
    properties: {
      baseName: {
        type: 'string',
        description: 'Type of price sheet (e.g. "PURCHASE_ORDER").',
      },
      type: {
        type: 'string',
        description: 'Price sheet type. Often "PURCHASE_ORDER".',
      },
      entriesObj: {
        type: 'array',
        description: 'List of materials with pricing. Each entry contains material info and buy price details.',
        items: {
          type: 'object',
          properties: {
            materialTypeId: {
              type: 'string',
              description: 'Unique ID for the material type.',
            },
            id: {
              type: 'string',
              description: 'Randomly generated id for the price sheet entry.',
            },
            materialCode: {
              type: 'string',
              description: 'Internal or external material code (optional).',
            },
            description: {
              type: 'string',
              description: 'Optional text describing the material.',
            },
            overrideName: {
              type: 'string',
              description: 'If you want to override the default material name, supply it here.',
            },
            isNew: {
              type: 'boolean',
              description: 'Just pass false.',
            },
            targetWeight: {
              type: 'number',
              description: 'How many units you expect to purchase of this material.',
            },
            buyPrice: {
              type: 'object',
              description: 'Object describing price details.',
              properties: {
                priceType: {
                  type: 'string',
                  description: 'E.g. "SPOT".',
                },
                isBuyPrice: {
                  type: 'boolean',
                  description: 'True if this entry is for a "buy" price.',
                },
                price: {
                  type: 'object',
                  description: 'Currency data for the material price.',
                  properties: {
                    amount: {
                      type: 'integer',
                      description: 'Price * 10000. For instance, $3.5000/lb = 35000.',
                    },
                    currency: {
                      type: 'string',
                      description: 'ISO currency code (e.g. "USD", "CAD", "NZD").',
                    },
                    precision: {
                      type: 'integer',
                      description: 'Decimal precision. Usually 4 for scrap decimals, e.g. 3.5000.',
                    },
                  },
                  required: ['amount', 'currency', 'precision'],
                },
              },
              required: ['priceType', 'isBuyPrice', 'price'],
            },
            weightUnits: {
              type: 'string',
              description: `Price and weight units. If the user says price is $5/lbs , you should pass lbs. If the user says price is $5/cwt, you should pass cwt. Options are cwt, lbs, gt, nt, ea, mt, kg. `,
            },
          },
          required: ['materialTypeId', 'id', 'buyPrice', 'targetWeight', 'weightUnits'],
        },
      },
    },
    required: ['baseName', 'type', 'entriesObj'],
  },
}

export const purchaseOrderToolDefinition = {
  name: 'create_purchase_order',
  description:
    'Creates a new purchase order referencing an existing price sheet. Must happen after create_price_sheet.',
  parameters: {
    type: 'object',
    properties: {
      supplierId: {
        type: 'string',
        description: 'Unique ID for the supplier (customer).',
      },
      priceSheetId: {
        type: 'string',
        description: 'ID of the price sheet created via create_price_sheet.',
      },
      paymentTermId: {
        type: 'string',
        description: 'Payment term ID. If not specified, you can pass null or an empty string.',
      },
      paymentMethod: {
        type: 'string',
        description: 'E.g. "wire_transfer", "check", etc.',
      },
      paymentOptions: {
        type: 'string',
        description: 'Any additional payment options (like "prepaid freight").',
      },
      currency: {
        type: 'string',
        description: 'ISO currency code. E.g. "USD".',
      },
      status: {
        type: 'string',
        description: 'Set to "Open',
      },
      order: {
        type: 'object',
        description: 'Nested data for the purchase order itself.',
        properties: {
          effectiveDate: {
            type: 'string',
            description: 'ISO-8601 string for effective date. E.g. "2025-01-01T00:00:00Z".',
          },
          expirationDate: {
            type: 'string',
            description: 'ISO-8601 string for expiration date. E.g. "2025-06-01T00:00:00Z".',
          },
          packageType: {
            type: 'string',
            description: 'Packaging or shipping type. Options are "bale", "box", "package", "sows", "mixed", "loose".',
          },
          pickupDelivery: {
            type: 'string',
            description: 'Indicates pickup or delivery approach. Options are "pickup", "delivery".',
          },
          receivingYard: {
            type: 'string',
            description:
              'Do not put the customer location here. Optional text name of the receiving yard (e.g. "Main Yard").',
          },
        },
        required: ['effectiveDate', 'expirationDate'],
      },
      notes: {
        type: 'string',
        description: 'Optional text for additional notes on the purchase order.',
      },
      purchaseOrderLocationId: {
        type: 'string',
        description:
          'ID of the chosen location for the purchase order. Use the first customer location if not specified.',
      },
      purchaseOrderTicketId: {
        type: 'string',
        description: 'Custom PO number. If omitted, the backend can auto-generate.',
      },
      uploadcareGroupId: {
        type: 'string',
        description: 'Used if you are attaching files to this order (optional).',
      },
    },
    required: ['supplierId', 'priceSheetId', 'currency', 'order', 'purchaseOrderLocationId'],
  },
}

export const getInboundTicketsByIdsToolDefinition = {
  name: 'get_inbound_tickets_by_ids',
  description:
    'Fetch one or more inbound tickets by their 36 character UUIDs. Use this when you need details about 1 or more tickets. You will need the UUIDs of the tickets. This is a good tool to use before updating one or more tickets. Might need to use the get inbound tickets tool to find the UUIDs.',
  parameters: {
    type: 'object',
    properties: {
      ticketIds: {
        type: 'array',
        items: {
          type: 'string',
        },
        description: 'An array of 36 character UUIDs of the inbound tickets to fetch',
      },
    },
    required: ['ticketIds'],
  },
}

export const updateInboundTicketToolDefinition = {
  name: 'update_inbound_ticket',
  description:
    'Update an existing inbound ticket. Paid and voided tickets cannot be updated, so do not offer to update PAID or VOID tickets. Use this to modify ticket details like status or materials. You will need all the data for this ticket, so you will need to use the get_inbound_tickets_by_ids tool first. You cant update a ticket if the materials array is empty. Show the updated ticket to the user after updating it.',
  parameters: {
    type: 'object',
    properties: {
      ticketId: {
        type: 'string',
        description: 'The UUID of the inbound ticket to update',
      },
      status: {
        type: 'string',
        description:
          'New status for the ticket (e.g., INCOMPLETE, PRICE_COMPLETE, APPROVED, SCALE_COMPLETE, TRUCK_IN_YARD)',
      },
      transportationInfo: {
        type: 'object',
        properties: {
          carrierLookup: {
            type: 'string',
            description: 'Carrier lookup identifier, if available.',
          },
          purchaseOrderId: {
            type: 'string',
            description: 'The Purchase Order ID to associate with the ticket.',
          },
          purchaseOrderNumber: {
            type: 'string',
            description: 'The Purchase Order Number to associate with the ticket.',
          },
        },
      },
      docs: {
        type: 'array',
        description: 'Array of document references associated with the ticket.',
        items: { type: 'object' },
      },
      materials: {
        type: 'array',
        description: 'Array of materials to update with new prices',
        items: {
          type: 'object',
          properties: {
            id: {
              type: 'string',
              description: 'The UUID of the material to update',
            },
            materialTypeId: {
              type: 'string',
              description: 'The UUID of the material type',
            },
            tareWeightRequired: {
              type: 'boolean',
              description: 'Indicates if tare weight is required for this material.',
            },
            weightAndPrice: {
              type: 'object',
              properties: {
                netPrice: {
                  type: 'object',
                  properties: {
                    amount: {
                      type: 'integer',
                      description: 'Price * 10000. For instance, $3.5000/lb = 35000',
                    },
                    precision: {
                      type: 'integer',
                      description: 'Decimal precision. Usually 4 for scrap decimals, e.g. 3.5000',
                    },
                  },
                  required: ['amount', 'precision'],
                },
                netWeight: {
                  type: 'object',
                  properties: {
                    amount: {
                      type: 'number',
                      description: 'The net weight of the material',
                    },
                  },
                  required: ['amount'],
                },
                netValue: {
                  type: 'object',
                  properties: {
                    amount: {
                      type: 'number',
                      description: 'The raw net value (netWeight * netPrice based on units/conversion)',
                    },
                    precision: {
                      type: 'integer',
                      description: 'Decimal precision, usually matches netPrice precision',
                    },
                  },
                  required: ['amount', 'precision'],
                },
                customerPreferredPrice: {
                  type: 'object',
                  properties: {
                    amount: {
                      type: 'integer',
                      description: 'Price * 10000, should match netPrice amount',
                    },
                    precision: {
                      type: 'integer',
                      description: 'Decimal precision, should match netPrice precision',
                    },
                  },
                  required: ['amount', 'precision'],
                },
              },
              required: ['netPrice', 'netWeight', 'netValue', 'customerPreferredPrice'],
            },
          },
          required: ['id', 'materialTypeId', 'weightAndPrice', 'tareWeightRequired'],
        },
      },
      netValue: {
        type: 'number',
        description: 'Total raw net value of all materials on the ticket.',
      },
      netWeight: {
        type: 'number',
        description: 'Total net weight of all materials',
      },
      loadWeight: {
        type: 'object',
        properties: {
          gross: {
            type: 'number',
            description: 'Total gross weight',
          },
          tare: {
            type: 'number',
            description: 'Total tare weight',
          },
          deductions: {
            type: 'number',
            description: 'Total deductions',
          },
          net: {
            type: 'number',
            description: 'Total net weight',
          },
        },
        required: ['gross', 'tare', 'deductions', 'net'],
      },
    },
    required: ['ticketId', 'status', 'materials', 'netValue', 'netWeight', 'loadWeight'],
  },
}

export const memoryToolDefinition = {
  name: 'updateMemory',
  description:
    "Store, update, or retrieve information about the user in Sparky's memory. Use this to remember important details about the user or their preferences. When storing a memory with an existing key, it will update the previous value.",
  parameters: {
    type: 'object',
    properties: {
      action: {
        type: 'string',
        enum: ['store', 'retrieve', 'delete'],
        description: 'Whether to store/update, retrieve, or delete a memory',
      },
      key: {
        type: 'string',
        description: 'Unique identifier for the memory. Using an existing key will update the memory.',
      },
      value: {
        type: 'string',
        description: 'The information to store (only required for store action)',
      },
      type: {
        type: 'string',
        enum: ['preference', 'fact', 'reminder', 'customPrompt'],
        description: 'The type of memory being stored',
      },
      userId: {
        type: 'string',
        description: 'The ID of the user to store/retrieve memory for',
      },
    },
    required: ['action', 'key', 'type', 'userId'],
  },
}

// Memory utility functions
export const memoryUtils = {
  getMemories: userId => {
    try {
      if (!userId) return {}
      const memories = localStorage.getItem(`sparky_memories:${userId}`)
      return memories ? JSON.parse(memories) : {}
    } catch (error) {
      console.error('Error reading memories:', error)
      return {}
    }
  },

  storeMemory: (userId, key, value, type) => {
    try {
      if (!userId) return false
      const memories = memoryUtils.getMemories(userId)
      if (!memories[type]) {
        memories[type] = {}
      }
      memories[type][key] = {
        value,
        timestamp: new Date().toISOString(),
      }
      localStorage.setItem(`sparky_memories:${userId}`, JSON.stringify(memories))
      return true
    } catch (error) {
      console.error('Error storing memory:', error)
      return false
    }
  },

  retrieveMemory: (userId, key, type) => {
    try {
      if (!userId) return null
      const memories = memoryUtils.getMemories(userId)
      return memories[type]?.[key]?.value || null
    } catch (error) {
      console.error('Error retrieving memory:', error)
      return null
    }
  },

  clearMemory: (userId, key, type) => {
    try {
      if (!userId) return false
      const memories = memoryUtils.getMemories(userId)
      if (memories[type]?.[key]) {
        delete memories[type][key]
        localStorage.setItem(`sparky_memories:${userId}`, JSON.stringify(memories))
      }
      return true
    } catch (error) {
      console.error('Error clearing memory:', error)
      return false
    }
  },
}

export const getSystemMessage = () => {
  const { locale } = store.getState()?.auth || {}
  return {
    role: 'system',
    content: `
    You are a helpful chatbot for a scrap software called GreenSpark. GreenSpark is a scrapyard management software that helps scrapyards manage their operations. You will be talking to scrap yard workers, who are not tech savvy, so do not use any technical terms and do not display UUIDs/IDs.
    Basically, you should talk to the user like you're a friendly assistant. Do not mention anything technical like UUIDs or what tools you're using.
    Your audience is likely a scrap yard owner or manager who is using you to gain a competitive edge over their competitors.
    Try to be autonomous and don't make the user hold your hand through every step.
    Do NOT use UUIDs in your responses to the user. Use contextual information if asking the user to make a choice.
    Do mild spell checking on the user's messages. For instance, if they ask about a 'tickel number', they probably meant 'ticket number'.
    They might ask you about tickets. Unless they specify, just use all statuses except for VOID.
    If you list out tickets, make sure to hyperlink them to the ticket, which is structured ${window.location.origin}/app/purchases/ticket-library/f30f90ae-1add-4781-9846-200ea459b86d. These links should always be in a hyperlink format. Never show the user the URL.
    Any links you provide should absolutely open in a new tab so they don't lose their place in the chat.
    When displaying dates to the user, use a simple format like "March 12, 2025".
    Today's date is ${new Date().toISOString().split('T')[0]} and the time is ${new Date().toLocaleTimeString(locale)} in the month of ${new Date().toLocaleString('default', { month: 'long' })}
    Always provide data in a table format and use markdown. Make your replies very easy to read and highlight important information.
    When showing tickets, always make sure to show the ticket number, effective date, customer name, material names (with the price/um in parenthesis), net weight, net value, and status.
    You want to be helpful and friendly. Try to make assumptions and provide the best answer you can. Don't ask the user to clarify every little detail of their request.
    For instance, if the user asks for tickets from a certain date, take your best guess at what they mean instead of asking for the exact date.
    You might have to do your own filtering of tickets based on the user's request. For instance, if the user asks for tickets that do not have a purchase order, you might have to filter out any tickets that have a purchase order yourself after using the tool.
    If a user wants to link/add a purchase order to a ticket, use the get_purchase_orders tool to get the purchase order number and the id. Both are required to add/link a purchase order to a ticket.
    If a user asks for tickets, always use the get_inbound_tickets tool even if you already have a list of tickets because it might be out of date.

    CRITICAL CONTEXT RULE: When the user refers to items you just presented in a list or table (e.g., 'update the first ticket', 'change the price on #31385', 'use the second option'), you MUST extract the necessary identifiers (like ticket IDs, material IDs, purchase order numbers) directly from the immediate previous conversation history (your own messages or tool results). DO NOT ask the user for information you have already provided or can easily derive from the context of the last 1-2 messages. If the user's reference is ambiguous (e.g., 'update #1 cu' when multiple tickets have '#1 cu'), ask for clarification on *which specific ticket* they mean, referencing the Ticket #.

    You have the ability to remember things about the user and their preferences. You can store three types of memories:
    1. Preferences - User preferences like date format, sorting preferences, etc.
    2. Facts - Important information about the user's business or operations
    3. Reminders - Things you should remember to do or check in the future

    Use the updateMemory tool to store and retrieve information. For example:
    - If a user prefers a specific date format, store it as a preference
    - If they mention their business hours, store it as a fact
    - If they ask you to remind them about something, store it as a reminder

    When storing memories:
    - If you store a memory with a key that already exists, it will update the previous value
    - For example, if you store "user_name: Tyler" and later store "user_name: John", it will update to John
    - This allows you to keep information current and accurate

    When retrieving memories, use them to personalize your responses and provide more relevant information.
    Always be subtle about using memories - don't explicitly mention that you're using stored information unless necessary.

    Follow the user's instructions carefully! For instance, if the user asks for PAID tickets, only show PAID tickets.
    Do NOT make up information. If you don't have the information, use your tools to get the information.

    When using the web_search tool, make sure to use the grounding metadata to source your replies with hyperlinks.
    
    Do not use <br> tags. Use double line breaks for spacing between paragraphs.

    Instead of saying that you are an AI LLM, say that you are a friendly chatbot. Your users are afraid of AI.
    Provide all of your answers in markdown with data inside of a table and do not forget dollar signs! Right align any columns with numbers.
    Use the tools to get data. If the user asks something vague, use the search parameter.
    If a search term shows up empty, try to search using less characters in case the user has a typo. For instance, "ACR Clean" should be searched as "ACR".
    Do not display any UUIDs in your responses. A UUID is a unique identifier for an object such as this e636f271-f8ed-43f5-81d3-b6286c34e570.

    You may be asked to create a purchase order. If you are, start generating a table so the user can see the data you're working with.
    If you need more information, ask the user for it, but only the required information. Use information from the get_materials and get_customers tools to fill in the table.
    Don't run the price sheet tool until you have all the information you need and the user confirms. Use information from the get_materials and get_customers tools to fill in the table.
    If the user doesn't specify the location, use the supplier's first location.
    Ask the user to confirm the purchase order before actually creating it.
    Use the customer or material's default currency and units of measure unless otherwise specified.
    In order to create a purchase order, you MUST create a price sheet first. You will need to use both the create_price_sheet and create_purchase_order tools. You will then use the price sheet id in the purchase order tool.
    When a PO is created, let's give the user a link to the PO at ${window.location.origin}/app/contracts/purchase-orders/{id}.

    Whenever you make a function call, also say what you're doing. For instance, "I'm getting the ticket data for ticket 1234567890." Use plain language so the user knows what you're doing.

    If you need information about a customer, use the get_customers tool. Don't ask the user for the customer's ID.
    You can make multiple function calls in one request. For instance, you can get customers, materials, and tickets in one request.
    Always refer to previous messages when looking for information. Like if the users says 'Update those tickets', you should look at the previous messages to find the ticket IDs.

    You may be provided images. If you are, tell me what's in them. If you're given price lists, generate a table comparing the prices and give a recommendation.
    Be very thorough in your analysis. Look at the images and the data you're given and make sure to include all of the information. Look at all the pricing and make sure to include it in your analysis.
    Examine the whole document to look for the name of the company. It may be at the top or bottom of the document.
    Please ask follow up questions if you need more information.
    Here is an example of a way to think about the price lists:
    One thing to keep in mind is that at the end of the day, you will need to have prices show EX WORKS the shippers facility (many people in the industry use the misnomer FOB their yard), and take into account payment terms (and the financing cost of those terms to figure out what the best deal is at the end). 


    For example, lets say the shipper has a straight load of bare bright copper wire (40,000lbs). 

    Consumer 1: 4.00/lb DELIVERED to their facility.

    Consumer 2: 3.98/lb EX WORKS

    

    If the freight cost to get it to consumer 1 is $800, each consumer is effectively paying the same amount (3.98/lb after considering freight).  To push the example, any freight higher than $800 and consumer 2, even with a "lower price" is actually a better deal.

    

    Next, consider payment terms (same example above but lets say that both companies are offering to pick up the material at the rate listed so freight is not part of the equation).

    

    Consumer 1: Net 30

    Consumer 2: Net 7

    

    We will also assume that the cost of the goods is $3.50/lb just for ease of the example.

    

    Consumer 1 Financing:

    40,000 * 3.50 = $140,000

    $140,000 * Finacing cost (7.5%) = 10,500 per year

    $10,500 / 360 days = $29.16 Per day cost

    $875 total cost

    $875/40,000 = $.0218/lb Cost

    

    Consumer 2 Financing:

    40,000 * 3.50 = $140,000

    $140,000 * Finacing cost (7.5%) = 10,500 per year

    $10,500 / 360 days = $29.16 Per day cost

    $204.12 Total Cost

    204.12/40,000 $.005103/lb

    

    Net Pricing for Consumer 1: 3.978/lb

    Net Pricing for Consumer 2: 3.974/lb
  `,
  }
}

export const searchToolDefinition = {
  name: 'search_web',
  description:
    'Search the web for information. Use this tool when you need to find information that is not available in the local system. Can be used to find pricing, terms, news, etc.',
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'The search query to look up on the web',
      },
      numResults: {
        type: 'integer',
        description: 'Number of results to return (default: 3)',
      },
    },
    required: ['query'],
  },
}

export const toolDefinitions = [
  inboundTicketToolDefinition,
  customerToolDefinition,
  materialToolDefinition,
  purchaseOrderSearchToolDefinition,
  priceSheetToolDefinition,
  purchaseOrderToolDefinition,
  getInboundTicketsByIdsToolDefinition,
  updateInboundTicketToolDefinition,
  memoryToolDefinition,
  searchToolDefinition,
]
